import React, { useCallback, useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { Wrench, Plus, Trash2, Save, X, Edit3 } from "lucide-react";

export default function AdminConsultancy() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);   // service object or "new"
  const load = useCallback(async () => {
    try { const { data } = await api.get("/consultancy/services"); setItems(data); }
    catch (e) { toast.error(formatError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}"?`)) return;
    try { await api.delete(`/admin/consultancy/${s.slug}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6" data-testid="admin-consultancy-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold sm:text-4xl">Consultancy Catalogue</h1>
          <p className="mt-1 text-sm font-medium text-sarthi-muted">Edit prices, scope and specs without a redeploy.</p>
        </div>
        <button data-testid="new-service-btn" onClick={() => setEditing({ slug: "", category: "", title: "", short: "", scope: [], specs: [], amount: 2999, currency: "INR" })} className="sarthi-btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm text-white">
          <Plus className="h-4 w-4" /> New Service
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {items.map((s) => (
          <div key={s.slug} data-testid={`svc-${s.slug}`} className="sarthi-card p-4">
            <div className="text-xs font-bold uppercase text-sarthi-muted">{s.category}</div>
            <div className="mt-1 font-display text-lg font-extrabold">{s.title}</div>
            <div className="mt-1 text-sm text-sarthi-muted">{s.short}</div>
            <div className="mt-2 font-bold text-sarthi-primary">₹{s.amount} / session</div>
            <div className="mt-3 flex gap-2">
              <button data-testid={`edit-${s.slug}`} onClick={() => setEditing({ ...s })} className="sarthi-btn-outline inline-flex items-center gap-1 px-3 py-1 text-xs font-bold"><Edit3 className="h-3 w-3" /> Edit</button>
              <button data-testid={`del-${s.slug}`} onClick={() => del(s)} className="text-xs font-bold text-red-700 hover:text-red-900 underline">Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && <EditModal svc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function EditModal({ svc, onClose, onSaved }) {
  const isNew = !svc.slug || !svc.id;   // treat missing slug as new
  const [f, setF] = useState({ ...svc, scope: (svc.scope || []).join("\n"), specs: (svc.specs || []).join("\n") });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    const body = { ...f, amount: Number(f.amount), scope: f.scope.split("\n").map((s) => s.trim()).filter(Boolean), specs: f.specs.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      if (isNew) {
        if (!body.slug) return toast.error("Slug required (lowercase-with-hyphens)");
        await api.post("/admin/consultancy", body);
      } else {
        // Never PATCH slug — only mutable fields
        const patchBody = { ...body };
        delete patchBody.slug;
        delete patchBody.id;
        await api.patch(`/admin/consultancy/${svc.slug}`, patchBody);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="edit-svc-modal">
      <div className="sarthi-card w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between"><h2 className="font-display text-xl font-extrabold">{isNew ? "New Service" : `Edit — ${svc.slug}`}</h2><button onClick={onClose}><X className="h-5 w-5" /></button></div>
        <div className="grid grid-cols-2 gap-3">
          {isNew && <input data-testid="svc-slug" placeholder="slug-lowercase-hyphens" value={f.slug} onChange={set("slug")} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2 font-mono" />}
          <input data-testid="svc-category" placeholder="Category" value={f.category} onChange={set("category")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="svc-amount" type="number" placeholder="Amount (INR)" value={f.amount} onChange={set("amount")} className="rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <input data-testid="svc-title" placeholder="Title" value={f.title} onChange={set("title")} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <textarea data-testid="svc-short" placeholder="Short description" value={f.short} onChange={set("short")} rows={2} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2" />
          <textarea data-testid="svc-scope" placeholder="Scope items (one per line)" value={f.scope} onChange={set("scope")} rows={4} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2 font-mono text-sm" />
          <textarea data-testid="svc-specs" placeholder="Technical specs (one per line)" value={f.specs} onChange={set("specs")} rows={4} className="col-span-2 rounded-xl border-2 border-sarthi-border px-3 py-2 font-mono text-sm" />
        </div>
        <button data-testid="svc-save" onClick={save} className="sarthi-btn-primary mt-4 inline-flex w-full items-center justify-center gap-2 py-2 text-white"><Save className="h-4 w-4" /> Save</button>
      </div>
    </div>
  );
}
